/**
 * Canonical hint shapes — used everywhere a hint is described.
 *
 * Layering:
 *
 *   HintDiagnostics  ⊂  HintRecord  ⊂  HintHistoryEntry
 *   (LLM facts)         (user-facing)   (backend persistence + internals)
 *
 * Each layer is a strict superset of the previous. Adding a diagnostic
 * field is a one-place edit on `HintDiagnostics`; adding a per-hint
 * context field is a one-place edit on `HintContext`. `HintRecord`
 * (client + wire) and `HintHistoryEntry` (backend persistence) both
 * compose these blocks instead of redeclaring fields.
 *
 * `HintDiagnostics` and `HintContext` are public because the backend's
 * `HintHistoryEntry` extends them; they are also useful as input types
 * for narrow renderers (e.g. the in-session "Why this hint" panel only
 * needs `HintDiagnostics`).
 */

/** What kind of help the student is asking for. */
export type HintCategory =
    | 'next_step' | 'where_wrong' | 'explain_concept'
    | 'smaller_hint' | 'right_direction';

/** How direct the hint is. UI-facing four-bucket scale; the LLM-side
 *  schema uses longer names (`gentle_nudge`, etc.) which are mapped to
 *  these buckets at the wire boundary. */
export type HintLevel = 'nudge' | 'direction' | 'guided' | 'reveal';

/** A teacher/assignment hint control: a starting level, or `'none'` to disable
 *  hints entirely. Set on Student Controls (per subject) and per assignment.
 *  See docs/FeatureDesigns/HintSystem/teacher-hint-level-control.md. */
export type HintLevelSetting = 'none' | HintLevel;

/** Ordering for the setting (none most restrictive → reveal most direct). Used to
 *  compare levels and to pick the most-restrictive across multiple teachers. */
export const HINT_LEVEL_SETTING_ORDER: Record<HintLevelSetting, number> = {
    none: 0, nudge: 1, direction: 2, guided: 3, reveal: 4,
};

/** All selectable settings in display order. */
export const HINT_LEVEL_SETTINGS: HintLevelSetting[] = ['none', 'nudge', 'direction', 'guided', 'reveal'];

/** Value + display label for the hint-level dropdown. Single source of truth for
 *  every hint-level selector (Student Controls, per-student override, assignment
 *  settings, admin test config) so the wording stays consistent. */
export const HINT_LEVEL_SETTING_OPTIONS: { value: HintLevelSetting; label: string }[] = [
    { value: 'none', label: 'None (hints off)' },
    { value: 'nudge', label: 'Nudge (most Socratic)' },
    { value: 'direction', label: 'Direction' },
    { value: 'guided', label: 'Guided setup' },
    { value: 'reveal', label: 'Reveal next step (most direct)' },
];

/** Normalize a hint setting into a concrete `HintLevelSetting`: absent →
 *  `'nudge'`, the system-default start level (pre-feature behaviour). Single
 *  source of truth for reads.
 *
 *  It used to take a second argument, the legacy `allowHints` boolean, consulted
 *  only when `hintLevel` was absent. That case never occurred: every document
 *  carrying the boolean also carried `defaultHintLevel`, in all three containers
 *  and without exception, so the parameter was unreachable (migration 021). */
export function resolveHintLevelSetting(
    hintLevel: HintLevelSetting | undefined | null,
): HintLevelSetting {
    return hintLevel || 'nudge';
}

/** Whether a resolved setting enables hints at all. */
export function isHintsEnabled(setting: HintLevelSetting): boolean {
    return setting !== 'none';
}

/** Pick the most-restrictive of several settings (lowest order; `none` wins). */
export function mostRestrictiveHintLevel(settings: HintLevelSetting[]): HintLevelSetting {
    if (settings.length === 0) return 'nudge';
    return settings.reduce((min, s) =>
        HINT_LEVEL_SETTING_ORDER[s] < HINT_LEVEL_SETTING_ORDER[min] ? s : min);
}

/** Inferred student state at hint time, from the LLM hint_response schema. */
export type HintDetectedState =
    | 'no_attempt' | 'progressed' | 'stuck' | 'confused'
    | 'error' | 'needs_clarification';

/** Difficulty of the next unresolved step. */
export type HintStepDifficulty = 'easy' | 'medium' | 'hard';

/** Pure LLM-output diagnostics — facts the model inferred from the
 *  student's work. None of these are influenced by the student's request;
 *  they are descriptions the model produced. */
export interface HintDiagnostics {
    detectedState: HintDetectedState;
    detectedIssue: string;
    /** Second-person summary of student progress at hint time
     *  ("You have set up the equation and substituted u=20 m/s…").
     *  Optional during the back-compat window for older persisted hints
     *  that pre-date the field being saved. */
    yourProgress?: string;
    difficulty: HintStepDifficulty;
}

/** Per-hint inputs — everything the student or the resolver provided
 *  TO the LLM hint call. `category` is what the student asked for (kind
 *  of help); `level` is the resolved how-direct level; `studentMessage`
 *  is free-text the student attached; `typedTextAtHintTime` and
 *  `canvasImageUrl` snapshot what their work was. */
export interface HintContext {
    /** What kind of help the student asked for. Resolved by the backend
     *  (defaults to 'next_step' if not specified; may be inferred from
     *  prior hint history). */
    category: HintCategory;
    /** Resolved level for this hint — student's explicit override or
     *  auto-decided based on category + prior hints. */
    level: HintLevel;
    /** Free-text the student attached to the hint request, if any. */
    studentMessage?: string;
    /** Typed text the student had at hint time. */
    typedTextAtHintTime?: string;
    /** Resolved blob URL of the canvas/photo at hint time, if any. */
    canvasImageUrl?: string;
}

/** Student thumbs up/down on an LLM hint, plus an optional comment.
 *  Stored inline on the hint turn (`HintHistoryEntry.feedback`) and carried
 *  on the wire `HintRecord` so live session + attempt-detail can render the
 *  current rating. One rating per turn — re-rating replaces. Only LLM hints
 *  are rated; prewritten reveals are not persisted as hint turns. */
export interface HintFeedback {
    rating: 'positive' | 'negative';
    comment?: string;
    /** ISO timestamp of the most recent rating for this turn. */
    submittedAt: string;
}

/** An educator's verdict on a hint's quality, stored beside — never instead
 *  of — the student's `HintFeedback`.
 *
 *  They are different judgements and both matter: a student says whether the
 *  hint helped them, a teacher says whether it was a good hint. Collapsing them
 *  into one field would lose exactly the distinction the review views exist to
 *  show, so this is a separate optional field on the same hint turn — no new
 *  container, no new document, no migration.
 *
 *  One verdict per hint (the latest replaces), with the reviewer recorded so
 *  the UI can always say who and when. */
export interface HintReviewerFeedback {
    rating: 'positive' | 'negative';
    comment?: string;
    submittedAt: string;
    /** Who reviewed it — denormalized so rendering a timeline needs no user join. */
    reviewerId: string;
    reviewerName: string;
    reviewerRole: 'teacher' | 'admin';
}

/** A hint shown to the student. Discriminated by source: prewritten
 *  reveals carry only `hint` + `source` + timestamp; LLM reveals also
 *  carry the diagnostic block and the input snapshot. Used both at
 *  reveal time (live session, in `revealedHintsMeta`) and post-attempt
 *  (in the attempt-detail wire response). */
export type HintRecord =
    | {
        hint: string;
        source: 'prewritten';
        createdAt?: string;
        /** Position in `question.hints[]` — the stable key for prewritten-hint
         *  feedback (decoupled from any LLM turn). */
        hintIndex?: number;
        /** Student's thumbs up/down on this prewritten hint, if rated. */
        feedback?: HintFeedback;
    }
    | ({
        hint: string;
        source: 'llm';
        createdAt?: string;
        /** Student's thumbs up/down on this hint, if rated. */
        feedback?: HintFeedback;
    } & HintDiagnostics & HintContext);
